import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useParams, useNavigate } from 'react-router-dom'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNotify, useRefresh, useGetOne } from 'react-admin'
import { getAvailableDevices, getStreamFromDevice, handlePermissions } from '../../utils/media'
import { recordTestResult, getLabTestAdviceCodeFromResult } from '../../utils/progressKit'
import { formatDateToBrowserLocale } from '../../utils/helpers'
import { fetchResultImages } from '../details/helpers/kitDetailsHelpers'
import { client } from '../../providers/data/client'
import ImagePreview from '../common/ImagePreview'
import moment from 'moment'
import watermark from 'watermarkjs'
import { labTestAdviceCodeDetail } from '../../utils/constants'

const modifyCollectionDateForEdit = (collectionDate) => {
  const [month, day, year] = collectionDate.split('/')
  return `${year}-${month}-${day}`
}

const TestRecordPage = ({ edit = false }) => {
  const { id } = useParams() // Get kit ID from URL
  const notify = useNotify()
  const refresh = useRefresh()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Fetch kit data using react-admin hook
  const { data: modalState, isLoading } = useGetOne('kits', { id })

  const [resultRecord, setResultRecord] = useState({
    collectionDate: '',
    result: '',
    sampleGrade: '',
    note: '',
    providerNotes: '',
    adviceCode: '',
  })

  const [adviceCodeDisabled, setAdviceCodeDisabled] = useState(true)

  const [errorState, setErrorState] = useState({
    collectionDate: null,
    result: null,
    sampleGrade: null,
    note: null,
    providerNotes: null,
    adviceCode: null,
  })

  const [camerasList, setCamerasList] = useState([])
  const [selectedCamera, setSelectedCamera] = useState('')
  const [capturedImages, setCapturedImages] = useState([])
  const [existingImages, setExistingImages] = useState([])
  const [patientIdCard, setPatientIdCard] = useState([])
  const [imagePreviewImagePath, setImagePreviewImagePath] = useState('')
  const [imagePreviewState, setImagePreviewState] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isImagesLoading, setIsImagesLoading] = useState(false)
  const [showImageWarning, setShowImageWarning] = useState(false)
  const [cameraPermissions, setCameraPermissions] = useState({ video: false, audio: false })
  const [notesExpanded, setNotesExpanded] = useState(false)
  const [providerNotesExpanded, setProviderNotesExpanded] = useState(false)

  // Auto-expand notes accordion when there is an error on the note field
  useEffect(() => {
    if (errorState.note) {
      setNotesExpanded(true)
    }
  }, [errorState.note])
  const cameraStreamRef = useRef(null)

  // Initialize form when modalState is loaded
  useEffect(() => {
    if (modalState && edit) {
      setResultRecord({
        collectionDate: modalState.specimenCollectionDate ? modifyCollectionDateForEdit(modalState.specimenCollectionDate) : '',
        result: modalState.labTestResult && modalState.labTestResult !== 'none' ? modalState.labTestResult : '',
        sampleGrade: modalState.labTestSampleGrade ? modalState.labTestSampleGrade : '',
        note: modalState.notes ? modalState.notes : '',
        providerNotes: modalState.labTestProviderNotes ? modalState.labTestProviderNotes : '',
        adviceCode: modalState.labTestAdviceCode ? modalState.labTestAdviceCode : '',
      })
    }
  }, [modalState, edit])

  const handleImagePreview = (bool) => {
    setImagePreviewState(bool)
  }

  // Check if there are any validation errors
  const hasValidationErrors = () => {
    return Object.values(errorState).some((error) => error !== null)
  }

  const clearCamera = useCallback(() => {
    // Stop stream from ref if available
    if (cameraStreamRef.current) {
      const tracks = cameraStreamRef.current.getTracks()
      tracks.forEach((track) => {
        track.stop()
      })
      cameraStreamRef.current = null
    }

    // Also stop stream from video element
    const videoElement = document.getElementById('camera-video')
    if (videoElement && videoElement.srcObject) {
      const tracks = videoElement.srcObject.getTracks()
      tracks.forEach((track) => {
        track.stop()
      })
      videoElement.srcObject = null
    }
    setSelectedCamera('')
  }, [])

  const clearStates = useCallback(() => {
    setCapturedImages([])
    setResultRecord({
      collectionDate: '',
      result: '',
      sampleGrade: '',
      note: '',
      providerNotes: '',
      adviceCode: '',
    })
    setCamerasList([])
    setCameraPermissions({ video: false, audio: false })
    clearCamera()
  }, [clearCamera])

  useEffect(() => {
    if (!modalState) return

    const { collectionDate, result, sampleGrade } = resultRecord
    const patientDoV = modalState?.patientDoV

    const updateAdviceCode = () => {
      if (result && sampleGrade) {
        setAdviceCodeDisabled(false)

        // Use 0.0 weeks if no collection date is provided
        const weeksSinceVasectomy = collectionDate ? calculateWeeksSinceVasectomy(patientDoV, collectionDate) : '0.0'

        const newAdviceCode = getLabTestAdviceCodeFromResult({
          weeksSinceVasectomy,
          labTestResult: result,
          labTestSampleGrade: sampleGrade,
        })

        if (newAdviceCode) {
          setResultRecord((prev) => {
            if (prev.adviceCode !== newAdviceCode) {
              return { ...prev, adviceCode: newAdviceCode }
            }
            return prev
          })

          // Clear advice code error since it's automatically calculated
          if (errorState.adviceCode) {
            setErrorState((prev) => ({
              ...prev,
              adviceCode: null,
            }))
          }
        }
      } else {
        setAdviceCodeDisabled(true)
        setResultRecord((prev) => {
          if (prev.adviceCode !== '') {
            return { ...prev, adviceCode: '' }
          }
          return prev
        })
      }
    }

    updateAdviceCode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultRecord.collectionDate, resultRecord.result, resultRecord.sampleGrade, modalState?.patientDoV, errorState.adviceCode])

  useEffect(() => {
    if (edit && modalState) {
      ; (async () => {
        setIsImagesLoading(true)
        try {
          const images = await fetchResultImages(modalState.kitId)
          setExistingImages(images)
        } catch (error) {
          // It's okay if images fail to load for a new test or if they don't exist
          console.log('No existing images found or error fetching them:', error)
        } finally {
          setIsImagesLoading(false)
        }
      })()
    }
  }, [edit, modalState])

  useEffect(() => {
    existingImages.forEach((image) => {
      if (image.isPatientIdCard) setPatientIdCard((prev) => [...prev, image.url])
    })
  }, [existingImages])

  useEffect(() => {
    ; (async () => {
      const permissions = await handlePermissions()
      setCameraPermissions(permissions)

      if (permissions.video) {
        const lastDeviceId = localStorage.getItem('selectedDeviceId')
        if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
          const devices = await getAvailableDevices()
          setCamerasList(devices)
          // Set to last deviceId, if any
          if (lastDeviceId) {
            setSelectedCamera(lastDeviceId)
          } else if (devices.length > 0) {
            setSelectedCamera(devices[0].deviceId)
          }
        } else {
          notify("Browser doesn't support cameras!", { type: 'error' })
        }
      } else {
        notify('Camera permission is required for image capture. Please grant permission and reload the page.', { type: 'error' })
      }
    })()

    return () => {
      clearCamera()
      clearStates()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCameraSelect = useCallback(
    async (deviceId) => {
      try {
        localStorage.setItem('selectedDeviceId', deviceId)
        const videoElement = document.getElementById('camera-video')
        setSelectedCamera(deviceId)

        if (!videoElement) {
          console.warn('Video element not found, camera may not be initialized yet')
          return
        }

        // Clear any existing stream
        if (videoElement.srcObject) {
          const tracks = videoElement.srcObject.getTracks()
          tracks.forEach((track) => track.stop())
          videoElement.srcObject = null // Ensure previous stream is fully released
        }

        const stream = await getStreamFromDevice(deviceId)
        if (videoElement && stream) {
          // Store stream in ref for reliable cleanup
          cameraStreamRef.current = stream
          videoElement.srcObject = stream
          await videoElement.play()
        } else {
          // Only show error if stream is not available
          notify('Error accessing camera. Please check permissions and try again.', { type: 'error' })
        }
      } catch (error) {
        // Only log error, do not notify user for transient errors
        console.error('Error accessing camera:', error)
      }
    },
    [notify]
  )

  // Initialize camera when selected and permissions are available
  useEffect(() => {
    if (selectedCamera && cameraPermissions.video) {
      // Small delay to ensure video element is rendered
      const timer = setTimeout(() => {
        handleCameraSelect(selectedCamera)
      }, 100)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [selectedCamera, cameraPermissions.video, handleCameraSelect])

  // Cleanup camera on component unmount and page navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      clearCamera()
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearCamera()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearCamera()
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [clearCamera])

  // Cleanup camera on component unmount - this is the most reliable
  useEffect(() => {
    return () => {
      // Force cleanup on unmount
      if (cameraStreamRef.current) {
        const tracks = cameraStreamRef.current.getTracks()
        tracks.forEach((track) => {
          track.stop()
        })
        cameraStreamRef.current = null
      }
      const videoElement = document.getElementById('camera-video')
      if (videoElement && videoElement.srcObject) {
        const tracks = videoElement.srcObject.getTracks()
        tracks.forEach((track) => {
          track.stop()
        })
        videoElement.srcObject = null
      }
    }
  }, [])

  const valid = () => {
    let result = true
    const errorObj = {
      collectionDate: null,
      result: null,
      sampleGrade: null,
      note: null,
      providerNotes: null,
      adviceCode: null,
    }

    if (resultRecord.result === 'error' && resultRecord.note === '') {
      errorObj.note = "Note is required when test result is 'error'"
      result = false
    }

    if (resultRecord.result === '') {
      errorObj.result = 'Result is required'
      result = false
    }
    if (resultRecord.sampleGrade === '') {
      errorObj.sampleGrade = 'Sample Grade is required'
      result = false
    }
    if (resultRecord.adviceCode === '') {
      errorObj.adviceCode = 'Advice Code is required'
      result = false
    }
    setErrorState(errorObj)
    return result
  }

  const handleSave = async (forceSubmit = false) => {
    setIsSaving(true)
    try {
      if (!valid()) {
        setIsSaving(false)
        return
      }

      const totalImages = existingImages.length + capturedImages.length

      // Check if user has less than 4 images and hasn't confirmed yet
      if (totalImages < 4 && !forceSubmit) {
        setIsSaving(false)
        setShowImageWarning(true)
        return
      }

      clearCamera()
      await recordTestResult({ ...resultRecord, images: capturedImages }, patientIdCard, modalState, notify, refresh, navigate)
      setIsSaving(false)
      // Navigate back to kit list after save
      navigate('/kits')
    } catch (error) {
      setIsSaving(false)
      console.log(error)

      // Provide more specific error messages based on the error type
      let errorMessage = 'Failed to save test record. Please try again.'

      if (error.response) {
        // First check if we have a specific error message from the backend
        if (error.response.data?.errorMessage) {
          // Direct error message from backend
          errorMessage = error.response.data.errorMessage
        }
        // If no specific error message, fallback to status-based messages
        else if (error.response.status === 413) {
          errorMessage = 'Images are too large. Please reduce image size or number of images and try again.'
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid data provided. Please check all fields and try again.'
        } else if (error.response.status === 500) {
          errorMessage = 'Server error occurred. Please try again in a few moments.'
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (error.message?.includes('Failed to fetch')) {
        errorMessage = 'Network connection failed. Please check your internet connection.'
      }

      notify(errorMessage, { type: 'error' })
    }
  }

  const handleConfirmSave = async () => {
    setShowImageWarning(false)
    await handleSave(true)
  }

  const handleDelete = async (image) => {
    if (patientIdCard.includes(image)) {
      setPatientIdCard(patientIdCard.filter((i) => i !== image))
    }

    if (image.includes('http') && edit) {
      const tempList = [...existingImages]
      const deleteableImage = tempList.splice(
        existingImages.findIndex((i) => i.url === image),
        1
      )
      const splitedKey = deleteableImage[0].key.split('/')
      const response = await client().delete(`kits/${modalState?.kitId}/artifact/${splitedKey[splitedKey.length - 1]}`)
      if (response.data.success) setExistingImages(tempList)
      return
    }

    const tempList = [...capturedImages]
    tempList.splice(capturedImages.indexOf(image), 1)
    setCapturedImages(tempList)
    return
  }

  const handleInput = (target) => {
    const { name, value } = target

    // Clear the error for this field when user provides input
    if (errorState[name] && value !== '') {
      setErrorState((prev) => ({
        ...prev,
        [name]: null,
      }))
    }

    if (name === 'result') {
      let clearSampleGradeError = false
      // Clear result error when a valid result is selected
      if (errorState.result && value !== '') {
        setErrorState((prev) => ({
          ...prev,
          result: null,
        }))
      }

      // Clear note error when changing from 'error' to other values
      if (resultRecord.result === 'error' && value !== 'error' && errorState.note) {
        setErrorState((prev) => ({
          ...prev,
          note: null,
        }))
      }

      switch (value) {
        case 'NSS':
          setResultRecord({
            ...resultRecord,
            [name]: value,
            sampleGrade: 'NONE',
          })
          clearSampleGradeError = true
          break
        case 'Positive':
          setResultRecord({
            ...resultRecord,
            [name]: value,
            sampleGrade: 'HIGH',
          })
          clearSampleGradeError = true
          break
        case 'error':
          setResultRecord({
            ...resultRecord,
            [name]: value,
            sampleGrade: 'NONE',
          })
          clearSampleGradeError = true
          break
        default:
          break
      }

      if (clearSampleGradeError) {
        setErrorState((prev) => ({
          ...prev,
          sampleGrade: null,
        }))
      }
      return
    }

    // Special handling for note field when result is 'error'
    if (name === 'note' && resultRecord.result === 'error' && value !== '') {
      setErrorState((prev) => ({
        ...prev,
        note: null,
      }))
    }

    setResultRecord({
      ...resultRecord,
      [name]: value,
    })
  }

  const calculateWeeksSinceVasectomy = (vasectomyDate, collectionDate) => {
    // Return 0 if either date is missing
    if (!vasectomyDate || !collectionDate) {
      return '0.0'
    }

    let vasectomyMoment, collectionMoment

    // Handle vasectomy date - could be unix timestamp or date string
    if (typeof vasectomyDate === 'number' || (typeof vasectomyDate === 'string' && !isNaN(vasectomyDate))) {
      // Unix timestamp
      vasectomyMoment = moment.unix(Number(vasectomyDate))
    } else {
      // Date string - try common formats
      const formats = ['MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', 'MM.DD.YY', 'M.DD.YY', 'MM/DD/YY', 'YYYYMMDD']
      vasectomyMoment = moment(vasectomyDate, formats, true)
    }

    // Handle collection date - likely in YYYY-MM-DD format from date input
    if (typeof collectionDate === 'number' || (typeof collectionDate === 'string' && !isNaN(collectionDate))) {
      // Unix timestamp
      collectionMoment = moment.unix(Number(collectionDate))
    } else {
      // Date string - try common formats, prioritizing YYYY-MM-DD for HTML date inputs
      const formats = ['YYYY-MM-DD', 'MM/DD/YYYY', 'MM-DD-YYYY', 'MM.DD.YY', 'M.DD.YY', 'MM/DD/YY', 'YYYYMMDD']
      collectionMoment = moment(collectionDate, formats, true)
    }

    // Validate that both dates are valid
    if (!vasectomyMoment.isValid() || !collectionMoment.isValid()) {
      console.warn('Invalid dates provided to calculateWeeksSinceVasectomy:', {
        vasectomyDate,
        collectionDate,
        vasectomyMoment: vasectomyMoment.format(),
        collectionMoment: collectionMoment.format(),
      })
      return '0.0'
    }

    // Calculate difference in days and convert to weeks
    const diffInDays = collectionMoment.diff(vasectomyMoment, 'days')
    const weeks = diffInDays / 7

    // Return as string with 1 decimal place
    return weeks.toFixed(1)
  }

  if (isLoading || !modalState) {
    return <Box sx={{ p: 3 }}>Loading...</Box>
  }

  const isExpired = modalState?.expirationDate && moment(modalState.expirationDate, 'MM/DD/YYYY').isBefore(moment(), 'day')

  return (
    <>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            KitId {modalState?.kitId} - Record Test
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              clearCamera()
              navigate(-1)
            }}
            startIcon={<i className="fa-solid fa-arrow-left"></i>}
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Back to Kits
          </Button>
        </Box>
        <hr style={{ margin: '8px 0' }} />

        {isExpired && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              bgcolor: '#ffebee',
              color: '#c62828',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <i className="fa-solid fa-triangle-exclamation"></i>
            <Typography variant="h6" component="span" fontWeight="bold">
              This kit is expired
            </Typography>
          </Box>
        )}

        {/* Test Data Entry Section */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            {/* Patient Information Section */}
            <Grid item xs={12}>
              <Box sx={{ p: { xs: 1.5, sm: 1 }, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: { xs: 1.5, sm: 1 }, backgroundColor: '#afd4f8ff', borderRadius: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' }, fontWeight: 'bold', wordBreak: 'break-word' }}
                      >
                        <strong>Name:</strong> {modalState?.patientName}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: { xs: 1.5, sm: 1 }, backgroundColor: '#afd4f8ff', borderRadius: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' }, fontWeight: 'bold', wordBreak: 'break-word' }}
                      >
                        <strong>Date of Birth:</strong> {formatDateToBrowserLocale(modalState?.patientDoB)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: { xs: 1.5, sm: 1 }, backgroundColor: '#afd4f8ff', borderRadius: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' }, fontWeight: 'bold', wordBreak: 'break-word' }}
                      >
                        <strong>Date of Vasectomy:</strong> {formatDateToBrowserLocale(modalState?.patientDoV)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Collection Date and Calculation */}
            <Grid item xs={12} md={6} lg={4}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'flex-end' },
                  gap: 2,
                }}
              >
                <FormControl sx={{ flex: { xs: '1 1 auto', sm: '0 1 auto' } }}>
                  <FormLabel sx={{ mb: 1, fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Collection Date
                  </FormLabel>
                  <TextField
                    name="collectionDate"
                    onChange={(event) => handleInput(event.target)}
                    value={resultRecord.collectionDate}
                    type="date"
                    variant="outlined"
                    fullWidth
                    helperText={errorState.collectionDate}
                    error={errorState.collectionDate ? true : false}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0], // Disable future dates
                    }}
                  />
                </FormControl>
                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: '#e3f2fd',
                    borderRadius: 1,
                    minWidth: { xs: '100%', sm: 180 },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, fontWeight: 'bold', textAlign: { xs: 'center', sm: 'left' } }}
                    className="weeksSinceVas"
                  >
                    <strong>
                      {resultRecord.collectionDate !== ''
                        ? calculateWeeksSinceVasectomy(modalState?.patientDoV, resultRecord.collectionDate)
                        : '0.0'}
                    </strong>{' '}
                    weeks since vasectomy
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Test Result */}
            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1, fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>Test Result</FormLabel>
                <RadioGroup
                  name="result"
                  onChange={(event) => handleInput(event.target)}
                  className="radio-group"
                  value={resultRecord.result}
                >
                  <FormControlLabel value="NSS" control={<Radio />} label={<span className="btn-sm bg-success">NSS</span>} />
                  <FormControlLabel
                    value="Positive"
                    control={<Radio />}
                    label={<span className="btn-sm bg-danger">Positive</span>}
                  />
                  <FormControlLabel value="error" control={<Radio />} label={<span className="btn-sm bg-warning">Error</span>} />
                </RadioGroup>
                <FormHelperText error={errorState.result ? true : false}>{errorState.result}</FormHelperText>
              </FormControl>
            </Grid>

            {/* Sample Grade */}
            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1, fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Test Sample Grade
                </FormLabel>
                <Select
                  name="sampleGrade"
                  onChange={(event) => handleInput(event.target)}
                  value={resultRecord.sampleGrade}
                  variant="outlined"
                >
                  <MenuItem value="NONE">NONE (0/hpf)</MenuItem>
                  <MenuItem value="RARE">RARE (&nbsp;1/hpf)</MenuItem>
                  <MenuItem value="MODERATE">MODERATE (~1-10/hpf)</MenuItem>
                  <MenuItem value="HIGH">HIGH (10+/hpf)</MenuItem>
                </Select>
                <FormHelperText error={errorState.sampleGrade ? true : false}>{errorState.sampleGrade}</FormHelperText>
              </FormControl>
            </Grid>

            {/* Advice Code */}
            <Grid item xs={12} md={6} lg={4}>
              <FormLabel sx={{ mb: 1, fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>Advice Code</FormLabel>
              <FormControl margin="none" fullWidth>
                <Select
                  name="adviceCode"
                  onChange={(event) => handleInput(event.target)}
                  value={resultRecord.adviceCode}
                  disabled={adviceCodeDisabled}
                  variant="outlined"
                  sx={{ height: '48px' }}
                >
                  {labTestAdviceCodeDetail.map((item) => {
                    return (
                      <MenuItem key={item.value} value={item.value}>
                        {item.value}
                      </MenuItem>
                    )
                  })}
                </Select>
                <FormHelperText error={errorState.adviceCode ? true : false}>{errorState.adviceCode}</FormHelperText>
              </FormControl>
            </Grid>

            {/* Notes Section */}
            <Grid item xs={12} md={6} lg={4}>
              <FormLabel
                sx={{ mb: 1, fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}
                error={errorState.note ? true : false}
              >
                Notes (MFH Only){resultRecord.result === 'error' ? ' *' : ''}
              </FormLabel>
              <Accordion
                expanded={notesExpanded}
                onChange={(event, isExpanded) => setNotesExpanded(isExpanded)}
                sx={{
                  border: errorState.note ? '1px solid #d32f2f' : '1px solid transparent',
                  borderRadius: '4px !important',
                  transition: 'border-color 0.2s',
                }}
              >
                <AccordionSummary expandIcon={<i className="fa-solid fa-chevron-down"></i>}>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: errorState.note && !resultRecord.note ? '#d32f2f' : 'inherit',
                    }}
                  >
                    {resultRecord.note
                      ? resultRecord.note.length > (isMobile ? 30 : 40)
                        ? resultRecord.note.slice(0, isMobile ? 30 : 40) + '...'
                        : resultRecord.note
                      : errorState.note
                        ? 'Required — please enter a note'
                      : ''}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth>
                    <TextField
                      multiline
                      rows={4}
                      name="note"
                      onChange={(event) => handleInput(event.target)}
                      value={resultRecord.note}
                      variant="outlined"
                      error={errorState.note ? true : false}
                      helperText={errorState.note}
                    />
                  </FormControl>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <FormLabel sx={{ mb: 1, fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>Provider Notes</FormLabel>
              <Accordion expanded={providerNotesExpanded} onChange={(event, isExpanded) => setProviderNotesExpanded(isExpanded)}>
                <AccordionSummary expandIcon={<i className="fa-solid fa-chevron-down"></i>}>
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {resultRecord.providerNotes
                      ? resultRecord.providerNotes.length > (isMobile ? 30 : 40)
                        ? resultRecord.providerNotes.slice(0, isMobile ? 30 : 40) + '...'
                        : resultRecord.providerNotes
                      : ''}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth>
                    <TextField
                      multiline
                      rows={4}
                      name="providerNotes"
                      onChange={(event) => handleInput(event.target)}
                      value={resultRecord.providerNotes}
                      variant="outlined"
                      error={errorState.providerNotes ? true : false}
                      helperText={errorState.providerNotes}
                    />
                  </FormControl>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {resultRecord.adviceCode !== '' && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    backgroundColor: '#f0f7ff',
                    padding: { xs: 1.5, sm: 2 },
                    borderRadius: 1,
                    border: '1px solid #e3f2fd',
                  }}
                >
                  {(() => {
                    const details = labTestAdviceCodeDetail.find((item) => item.value === resultRecord.adviceCode)
                    return (
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' }, wordBreak: 'break-word' }}>
                        <strong>{details.value}</strong> - <em>{details.use}</em>
                        <br />
                        {details.description}
                      </Typography>
                    )
                  })()}
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Image Capture Section */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            {/* Camera Preview Section */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                Image Capture
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column' }, width: '100%', gap: 2 }}>
                {/* Video Preview */}
                <Box sx={{ width: { xs: '100%' } }}>
                  <Box
                    className="video-container"
                    sx={{
                      border: '2px solid #e0e0e0',
                      borderRadius: 2,
                      p: { xs: 1, sm: 2 },
                      backgroundColor: '#fafafa',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <video
                      id="camera-video"
                      autoPlay
                      muted
                      playsInline
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        height: 'auto',
                        minHeight: '200px',
                        maxHeight: '300px',
                        borderRadius: '8px',
                        backgroundColor: '#000',
                      }}
                    ></video>
                  </Box>
                </Box>
                {/* Camera Controls */}
                <Box
                  sx={{
                    width: { xs: '100%' },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FormControl sx={{ width: '100%', mb: 2 }}>
                    <InputLabel>Select Camera</InputLabel>
                    <Select
                      value={selectedCamera}
                      label="Select Camera"
                      onChange={(event) => handleCameraSelect(event.target.value)}
                      disabled={!cameraPermissions.video}
                    >
                      {camerasList.map((camera, index) => {
                        return (
                          <MenuItem key={index} value={camera.deviceId} selected={index === 0}>
                            {camera.label || `Camera ${camerasList.indexOf(camera) + 1}`}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>

                  <Button
                    size="large"
                    onClick={async () => {
                      if (!cameraPermissions.video) {
                        notify('Camera permission is required. Please grant permission and reload the page.', {
                          type: 'error',
                        })
                        return
                      }

                      const video = document.getElementById('camera-video')
                      if (!video || !video.srcObject) {
                        notify('Camera is not active. Please select a camera first.', { type: 'error' })
                        return
                      }

                      const totalImages = existingImages.length + capturedImages.length
                      if (totalImages >= 20) {
                        notify('Maximum of 20 images allowed. Please delete some images before capturing more.', {
                          type: 'error',
                        })
                        return
                      }

                      const canvas = document.getElementById('img-canvas')
                      const ctx = canvas.getContext('2d')
                      canvas.width = 720
                      canvas.height = 480
                      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

                      // White transparent box on which we render the watermark text
                      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
                      ctx.fillRect(0, canvas.height - 30, 400, 30)

                      const tempList = [...capturedImages]
                      const imageDataUrl = canvas.toDataURL('image/png')
                      const imgWithWatermark = await watermark([imageDataUrl]).image(
                        watermark.text.lowerLeft(
                          `MFH Lab Services  KitID ${modalState?.kitId}  ${moment().format('MM/DD/YYYY')}`,
                          '14px courier new',
                          '#000',
                          0.8
                        )
                      )

                      tempList.push(imgWithWatermark.src)
                      setCapturedImages(tempList)
                    }}
                    variant="contained"
                    disabled={!selectedCamera || existingImages.length + capturedImages.length >= 20}
                    sx={{ px: { xs: 2, sm: 4 }, width: '100%' }}
                  >
                    Capture Image <i className="fa-solid fa-arrow-right" style={{ marginLeft: 8 }}></i>
                  </Button>

                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ textAlign: 'center', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {cameraPermissions.video ? '' : 'Camera permission is required. Please grant permission and reload the page.'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Captured Images Section */}
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {`Captured Images (${existingImages.length + capturedImages.length}/20)`}
                {existingImages.length + capturedImages.length >= 18 && (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ ml: 1, display: { xs: 'block', sm: 'inline' }, mt: { xs: 0.5, sm: 0 } }}
                  >
                    - Approaching limit
                  </Typography>
                )}
              </Typography>
              <Box className="img-capture-section">
                <Box
                  className="image-container"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    overflowY: 'auto',
                    maxHeight: '380px',
                    gap: { xs: 1 },
                    p: { xs: 1 },
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    backgroundColor: '#fafafa',
                  }}
                >
                  {isImagesLoading ? (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    existingImages.map((i) => i.url)
                  )
                    .concat(capturedImages)
                    .map((item, index) => {
                      return (
                        <Box
                          className="img-thumb-container"
                          key={index}
                          sx={{
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            p: 1,
                            backgroundColor: 'white',
                            width: { xs: '100%', sm: 'auto' },
                            maxWidth: { xs: '100%', sm: '220px' },
                            flex: { xs: '1 1 100%', sm: '0 1 auto' },
                          }}
                        >
                          <Box
                            className="img-thumb-actions"
                            sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <input
                                type="checkbox"
                                id={`test-img-${index}`}
                                name="patientIdCard"
                                value={item}
                                checked={patientIdCard.includes(item)}
                                onChange={(e) =>
                                  setPatientIdCard(
                                    e.target.checked
                                      ? [...patientIdCard, e.target.value]
                                      : patientIdCard.filter((i) => i !== e.target.value)
                                  )
                                }
                              />
                              <label htmlFor={`test-img-${index}`} style={{ fontSize: '12px' }}>
                                Patient ID Card
                              </label>
                            </Box>
                            <span
                              onClick={async () => await handleDelete(item)}
                              className="fa-solid fa-trash"
                              style={{ cursor: 'pointer', color: '#d32f2f', fontSize: '14px' }}
                              title="Delete image"
                            ></span>
                          </Box>
                          <img
                            className={'img-thumb' + (patientIdCard.includes(item) ? '-patientidcard' : '')}
                            key={index}
                            src={item}
                            onClick={() => {
                              setImagePreviewImagePath(item)
                              handleImagePreview(true)
                            }}
                            alt="result"
                            style={{
                              width: '100%',
                              maxWidth: '200px',
                              height: '125px',
                              objectFit: 'cover',
                              cursor: 'pointer',
                              borderRadius: '4px',
                            }}
                          />
                        </Box>
                      )
                    })}
                  {!isImagesLoading && existingImages.length + capturedImages.length === 0 && (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 4,
                        px: 2,
                        color: '#666',
                        border: '2px dashed #ccc',
                        borderRadius: 1,
                        width: '100%',
                      }}
                    >
                      <i className="fa-solid fa-camera" style={{ fontSize: '24px', marginBottom: '8px', display: 'block' }}></i>
                      <Typography variant="body2">No images captured yet</Typography>
                      <Typography variant="caption">Use the capture button to take photos</Typography>
                    </Box>
                  )}
                  <canvas style={{ display: 'none' }} id="img-canvas" width={200} height={200}></canvas>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            justifyContent: 'flex-end',
            gap: 2,
            mt: 3,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button
            size="medium"
            type="submit"
            onClick={async (event) => {
              event.preventDefault()
              await handleSave()
            }}
            disabled={isSaving || hasValidationErrors()}
            variant="contained"
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
            startIcon={
              hasValidationErrors() ? <i className="fa-solid fa-exclamation-triangle"></i> : <i className="fa-solid fa-save"></i>
            }
            color={hasValidationErrors() ? 'error' : 'primary'}
          >
            {isSaving ? 'Saving...' : 'Save Test Record'}
          </Button>
          <Button
            size="medium"
            onClick={() => {
              clearCamera()
              navigate('/kits')
            }}
            variant="outlined"
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      <ImagePreview imagePath={imagePreviewImagePath} open={imagePreviewState} setOpen={handleImagePreview} />

      {/* Image Warning Dialog */}
      <Dialog
        open={showImageWarning}
        onClose={() => setShowImageWarning(false)}
        aria-labelledby="image-warning-dialog-title"
        aria-describedby="image-warning-dialog-description"
      >
        <DialogTitle id="image-warning-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <i className="fa-solid fa-exclamation-triangle" style={{ color: '#ff9800' }}></i>
            Insufficient Images
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="image-warning-dialog-description">
            You currently have <strong>{existingImages.length + capturedImages.length}</strong> image(s) captured.
            <br />
            <br />
            It is recommended to capture at least <strong>4 images</strong> for proper test documentation.
            <br />
            <br />
            Would you like to continue saving without the recommended number of images, or go back to capture more?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowImageWarning(false)}
            variant="outlined"
            startIcon={<i className="fa-solid fa-arrow-left"></i>}
          >
            Go Back & Capture More
          </Button>
          <Button onClick={handleConfirmSave} variant="contained" color="warning" startIcon={<i className="fa-solid fa-save"></i>}>
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TestRecordPage
